import {
  LpBookDemoBlock,
  LpFooterBlock,
  LpGptBottomCtaBlock,
} from "@/puck/landingBlocks";
import type { LandingComponents } from "@/puck/blocks/blockTypes";
import { AI_MODEL_BRAND_OPTIONS } from "@/constants/aiModelBrands";
import {
  GPT_CTA_ICON_DEFAULT_SIZE,
  GPT_CTA_ICON_POSITION_OPTIONS,
} from "@/constants/gptCtaBackgroundIcons";
import {
  ICLOSED_COLLECTIONS_EVENT_URL,
  ICLOSED_EVENT_TITLE,
} from "@/utils/iclosed";

export const ctaConfigs: Pick<
  LandingComponents,
  "LpBookDemo" | "LpGptBottomCta" | "LpFooter"
> = {
  LpBookDemo: {
    label: "Book demo (marketing)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      iclosedEventUrl: { type: "text", label: "iClosed event URL" },
      iclosedEventTitle: { type: "text", label: "iClosed event title" },
      layout: {
        type: "radio",
        label: "Layout",
        options: [
          { label: "Scheduler panel", value: "scheduler" },
          { label: "Embed only", value: "embed" },
        ],
      },
      schedulerTabs: {
        type: "array",
        label: "Scheduler tabs",
        getItemSummary: (item) => item.label,
        defaultItemProps: { label: "Step" },
        arrayFields: { label: { type: "text" } },
      },
      poweredByLabel: { type: "text", label: "Powered by label" },
      eventTitle: {
        type: "text",
        label: "Event title (panel)",
        contentEditable: true,
      },
      eventDescription: {
        type: "textarea",
        label: "Event description (panel)",
        contentEditable: true,
      },
      durationLabel: { type: "text", label: "Duration badge" },
      benefits: {
        type: "array",
        getItemSummary: (item) => item.text,
        defaultItemProps: { text: "Benefit" },
        arrayFields: { text: { type: "text", contentEditable: true } },
      },
      presentersLabel: { type: "text", label: "Presenters title" },
      presentersNames: { type: "text", label: "Presenter names" },
      presenters: {
        type: "array",
        label: "Presenter avatars",
        getItemSummary: (item) => item.name,
        defaultItemProps: { name: "Presenter" },
        arrayFields: { name: { type: "text" } },
      },
    },
    defaultProps: {
      eyebrow: "Book your demo",
      titleLead: "See Gail on your",
      titleAccent: "book of business.",
      subtitle:
        "20 minutes on your data, your workflow, your first thing to automate.",
      iclosedEventUrl: ICLOSED_COLLECTIONS_EVENT_URL,
      iclosedEventTitle: ICLOSED_EVENT_TITLE,
      layout: "scheduler",
      schedulerTabs: [
        { label: "Pick a time" },
        { label: "Your info" },
        { label: "Confirm" },
      ],
      poweredByLabel: "Powered by iClosed",
      eventTitle: "Gail · Schedule Demo",
      eventDescription:
        "20 minutes on your data, your workflow, your first thing to automate. A real walkthrough on your book.",
      durationLabel: "20 min",
      benefits: [
        { text: "Live walkthrough on your data." },
        { text: "One workflow scoped end-to-end." },
        { text: "Build your own AI expert, live." },
        { text: "Honest answers if it's not the right fit." },
      ],
      presentersLabel: "You'll meet",
      presentersNames: "Sarah, David, or Marisol.",
      presenters: [{ name: "Sarah" }, { name: "David" }, { name: "Marisol" }],
    },
    render: LpBookDemoBlock,
  },
  LpGptBottomCta: {
    label: "GPT bottom CTA",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "text", contentEditable: true },
      titleAccent: {
        type: "text",
        label: "Title accent (highlighted, optional)",
        contentEditable: true,
      },
      titleTrail: {
        type: "text",
        label: "Title trail (after accent, optional)",
        contentEditable: true,
      },
      subtitle: { type: "textarea", contentEditable: true },
      ctaLabel: { type: "text", contentEditable: true },
      backgroundIcons: {
        type: "array",
        label: "Background AI logos",
        getItemSummary: (item) => `${item.brandId} · ${item.position}`,
        defaultItemProps: {
          brandId: "openai",
          position: "top-left",
          size: GPT_CTA_ICON_DEFAULT_SIZE,
        },
        arrayFields: {
          brandId: {
            type: "select",
            label: "Logo",
            options: AI_MODEL_BRAND_OPTIONS,
          },
          position: {
            type: "select",
            label: "Position",
            options: GPT_CTA_ICON_POSITION_OPTIONS,
          },
          size: { type: "number", label: "Size (px)", min: 24, max: 160 },
        },
      },
    },
    // Defaults reproduce the original block exactly. The new capabilities
    // (accent/trail title + background logos) default to inert values so Puck
    // does NOT inject them into pages that were saved before this improvement —
    // those pages fill missing props from here at render time. Opt in per page.
    defaultProps: {
      eyebrow: "Exclusive offer",
      title: "Start this week. A month on us — a $500/month value.",
      titleAccent: "",
      titleTrail: "",
      subtitle:
        "20 minutes with a specialist. Bring one workflow you want to move.",
      ctaLabel: "Learn about our exclusive offer today",
      backgroundIcons: [],
    },
    render: LpGptBottomCtaBlock,
  },
  LpFooter: {
    label: "Marketing footer",
    fields: {
      copyright: { type: "text" },
      privacyLabel: { type: "text" },
      termsLabel: { type: "text" },
    },
    defaultProps: {
      copyright: "© {{year}} Gail. All rights reserved.",
      privacyLabel: "Privacy",
      termsLabel: "Terms",
    },
    render: LpFooterBlock,
  },
};
